import { reactive, type ComputedRef, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer } from 'video.js';
import type { GIMovie } from 'src/models/geoItem';
import type {
  AnalyticsParams,
  BothLeftRight,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';
import {
  useMoviePlayerCompareModeVjs,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeVjs';
import {
  useMoviePlayerCompareModeReversePlay,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeReversePlay';
import {
  useMoviePlayerCompareModeControl,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeControl';
import {
  useMoviePlayerCompareModeMovieTransition,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeMovieTransition';
import {
  useMoviePlayerCompareModeTimeUpdate,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeTimeUpdate';

interface Deps {
  enqueueUiTask: (task: () => Promise<void> | void) => void;
}

interface MoviePlayerCompareModePlaybackContext {
  vid1: ComputedRef<string>;
  vid2: ComputedRef<string>;
  videoAspectRatio: number;
  getVjsForVid: (vid: string) => VideoJsPlayer | undefined;
  getVjsForLr: (lr: LeftRight) => VideoJsPlayer | undefined;
  getUIPlayState: (lr: LeftRight) => 'playing' | 'paused';
  playMovie: (lr_: BothLeftRight) => void;
  pauseMovie: (lr_: BothLeftRight) => void;
  playUI: (lr_: BothLeftRight) => void;
  pauseUI: (lr_: BothLeftRight) => void;
  tickMovieBackward: (lr_: BothLeftRight) => Promise<void>;
  tickMovieForward: (lr_: BothLeftRight) => Promise<void>;
  setCurrentMovie: (lr: LeftRight, movie: GIMovie, movieIdx: number, playOffsetMsec: number) => Promise<void>;
  playPreviousMovie: (leftRight: LeftRight) => Promise<void>;
  playNextMovie: (leftRight: LeftRight) => Promise<void>;
  setPlaySpeed: (lr_: BothLeftRight, val: number) => Promise<void>;
  setVolume: (lr_: BothLeftRight, val: number) => Promise<void>;
  playModeChange: (lr_: BothLeftRight, isPlayBackward: boolean) => void;
  resetToDefault: (lr: LeftRight) => Promise<void>;
  prepareVideoPlayers: () => Promise<void>;
  findFirstSeekPoint: (lr: LeftRight) => number;
}

// CompareMode用の再生コア
// （左右2ストリーム、逆再生、カメラ位置切替を含む命令的video.js制御）のファサード。
// 単一ストリーム版とは実装方式が根本的に異なるため独立したcomposableとして持つ。
// 実処理は以下5つのサブcomposableに分割し、
// 本関数はそれらをDI順に組み立てて配線するだけの薄いラッパーとする。
// 分割前と全く同じメンバー集合を返すことで、呼び出し元（MoviePlayerCompareMode/index.vue、
// useMoviePlayerCompareModeCamera/Seek等）を無修正のまま保つ。
//   vjs            : video.jsインスタンス生成/DOM配線（vjsMap/vjsMapLR所有）
//   reversePlay    : setIntervalによる逆再生駆動
//   control        : play/pause/tick/速度/音量等の共通再生制御
//   movieTransition: 前後の動画への切替・シーク位置解決
//   timeUpdate     : video.jsのtimeupdateハンドラ（シークバー/mgi/analytics）
// vjsはcontrol/timeUpdateの関数をimportせず、
// コールバックとしてprepareVideoPlayersの引数で受け取る設計にすることで、
// vjs→control→vjs、vjs→timeUpdate→movieTransition→vjsのような循環参照を避けている。
export function useMoviePlayerCompareModePlayback(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModePlaybackContext> {
  const analytics = reactive<AnalyticsParams>({ accumulatedPlayMsecs: 0, tmpCalcBase: -1 });
  const vjs = useMoviePlayerCompareModeVjs(state);
  const reversePlay = useMoviePlayerCompareModeReversePlay(state, {
    getVjsForLr: vjs.getVjsForLr,
  });
  const control = useMoviePlayerCompareModeControl(state, {
    enqueueUiTask: deps.enqueueUiTask,
    getVjsForLr: vjs.getVjsForLr,
    playMovieBackward: reversePlay.playMovieBackward,
    pauseMovieBackward: reversePlay.pauseMovieBackward,
    resetReversePlayIntervals: reversePlay.resetIntervals,
  });
  const movieTransition = useMoviePlayerCompareModeMovieTransition(state, {
    getVjsForLr: vjs.getVjsForLr,
    updateTitleBarTexts: vjs.updateTitleBarTexts,
    getUIPlayState: control.getUIPlayState,
    pauseUI: control.pauseUI,
    playUI: control.playUI,
    analytics,
  });
  const timeUpdate = useMoviePlayerCompareModeTimeUpdate(state, {
    updateTitleBarTexts: vjs.updateTitleBarTexts,
    pauseUI: control.pauseUI,
    playNextMovie: movieTransition.playNextMovie,
    playPreviousMovie: movieTransition.playPreviousMovie,
    analytics,
  });
  // vjsは他の分割composableをimportしないため、
  // DOM/video.jsイベントから呼ぶハンドラをここでまとめて配線する。
  const prepareVideoPlayers = () => vjs.prepareVideoPlayers({
    onTimeUpdate: timeUpdate.onVjsTimeUpdate,
    onTickMovieBackward: () => control.tickMovieBackward('both'),
    onPlayUI: () => control.playUI('both'),
    onPauseUI: () => control.pauseUI('both'),
    onTickMovieForward: () => control.tickMovieForward('both'),
    onPlayModeChange: (isPlayBackward: boolean) => control.playModeChange('both', isPlayBackward),
  });

  return reactive({
    vid1: vjs.vid1,
    vid2: vjs.vid2,
    videoAspectRatio: vjs.videoAspectRatio,
    getVjsForVid: vjs.getVjsForVid,
    getVjsForLr: vjs.getVjsForLr,
    getUIPlayState: control.getUIPlayState,
    playMovie: control.playMovie,
    pauseMovie: control.pauseMovie,
    playUI: control.playUI,
    pauseUI: control.pauseUI,
    tickMovieBackward: control.tickMovieBackward,
    tickMovieForward: control.tickMovieForward,
    setCurrentMovie: movieTransition.setCurrentMovie,
    playPreviousMovie: movieTransition.playPreviousMovie,
    playNextMovie: movieTransition.playNextMovie,
    setPlaySpeed: control.setPlaySpeed,
    setVolume: control.setVolume,
    playModeChange: control.playModeChange,
    resetToDefault: control.resetToDefault,
    prepareVideoPlayers,
    findFirstSeekPoint: movieTransition.findFirstSeekPoint,
  });
}
